// src/components/Admin/ManageServices.js
import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../firebase';
import Overlay from '../Overlay';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage2';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';


function ManageServices() {
  const [servicesList, setServicesList] = useState([]);
  const [formState, setFormState] = useState({
    title: '',
    shortDescription: '',
    detailedDescription: '',
    imageFile: null,
    imageURL: '',
    id: null, // 編集時に使用
  });
  const [preview, setPreview] = useState(null); // プレビュー用
  const [showOverlay, setShowOverlay] = useState(false);

  // トリミング用の状態
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const snapshot = await db.collection("services").orderBy("title").get();
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setServicesList(data);
    } catch (error) {
      console.error("Error fetching services:", error);
      alert('サービスの取得に失敗しました。');
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    setFormState(prev => ({ ...prev, imageFile: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setIsCropping(true); // トリミングモードに切り替え
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      setIsCropping(false);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const { blob, fileUrl } = await getCroppedImg(preview, croppedAreaPixels, zoom, 1.5); // aspect=1.5 (300/200)
      setCroppedImage(blob);
      setPreview(fileUrl); // プレビューをトリミング後の画像に更新
      setIsCropping(false);
    } catch (e) {
      console.error(e);
      alert('画像のトリミングに失敗しました。');
    }
  }, [preview, croppedAreaPixels, zoom]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (formState.id) {
      await updateService();
    } else {
      await addService();
    }
  };

  const addService = async () => {
    try {
      if (!croppedImage) {
        alert('画像をアップロードしてトリミングしてください。');
        return;
      }

      const storageRef = storage.ref(`services/${Date.now()}_${formState.imageFile.name}`);
      await storageRef.put(croppedImage);
      const imageURL = await storageRef.getDownloadURL();

      await db.collection("services").add({
        title: formState.title,
        shortDescription: formState.shortDescription,
        detailedDescription: formState.detailedDescription,
        imageURL,
      });

      alert('サービスが追加されました。');
      resetForm();
      fetchServices();
    } catch (error) {
      console.error("Error adding service:", error);
      alert('サービスの追加に失敗しました。');
    }
  };

  const updateService = async () => {
    try {
      let imageURL = formState.imageURL;
      if (croppedImage) {
        const storageRef = storage.ref(`services/${Date.now()}_${formState.imageFile.name}`);
        await storageRef.put(croppedImage);
        imageURL = await storageRef.getDownloadURL();
      }

      await db.collection("services").doc(formState.id).update({
        title: formState.title,
        shortDescription: formState.shortDescription,
        detailedDescription: formState.detailedDescription,
        imageURL,
      });

      alert('サービスが更新されました。');
      resetForm();
      setShowOverlay(false);
      fetchServices();
    } catch (error) {
      console.error("Error updating service:", error);
      alert('サービスの更新に失敗しました。');
    }
  };

  const deleteService = async (id, imageURL) => {
    if (!window.confirm('このサービスを削除してもよろしいですか？')) return;
    try {
      await db.collection("services").doc(id).delete();
      if (imageURL) {
        const storageRef = storage.refFromURL(imageURL);
        await storageRef.delete();
      }
      alert('サービスが削除されました。');
      fetchServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      alert('サービスの削除に失敗しました。');
    }
  };

  const editService = service => {
    setFormState({
      title: service.title,
      shortDescription: service.shortDescription,
      detailedDescription: service.detailedDescription,
      imageFile: null,
      imageURL: service.imageURL || '',
      id: service.id,
    });
    setPreview(service.imageURL || null);
    setCroppedImage(null);
    setIsCropping(false);
  };

  const viewService = service => {
    setFormState({
      title: service.title,
      shortDescription: service.shortDescription,
      detailedDescription: service.detailedDescription,
      imageFile: null,
      imageURL: service.imageURL || '',
      id: service.id,
    });
    setShowOverlay(true);
  };

  const resetForm = () => {
    setFormState({
      title: '',
      shortDescription: '',
      detailedDescription: '',
      imageFile: null,
      imageURL: '',
      id: null,
    });
    setPreview(null);
    setCroppedImage(null);
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="manage-services">
      <h2>サービス紹介の管理</h2>
      <form onSubmit={handleSubmit}>
        <label>
          サービスタイトル:
          <input 
            type="text" 
            name="title" 
            value={formState.title} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          短い説明:
          <textarea 
            name="shortDescription" 
            value={formState.shortDescription} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          詳細な説明:
          <textarea 
            name="detailedDescription" 
            value={formState.detailedDescription} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          画像:
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange} 
            required={!formState.id} // 編集時は必須ではない
          />
        </label>

        {isCropping && preview && (
          <div className="crop-container">
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={3 / 2} // 300 / 200 = 1.5
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <div className="controls">
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, zoom) => setZoom(zoom)}
                style={{ width: '200px' }}
              />
              <Button variant="contained" color="primary" onClick={showCroppedImage} style={{ marginLeft: '10px' }}>
                トリミング
              </Button>
            </div>
          </div>
        )}

        {preview && !isCropping && (
          <div className="preview">
            <p>プレビュー:</p>
            <img 
              src={preview} 
              alt="Preview" 
              style={{ 
                width: '300px', 
                height: '200px', 
                objectFit: 'cover', 
                border: '2px solid #ccc' 
              }} 
            />
          </div>
        )}

        <Button type="submit" variant="contained" color="primary" className="button">
          {formState.id ? 'サービスを更新' : 'サービスを追加'}
        </Button>
        {formState.id && (
          <Button 
            type="button" 
            variant="outlined" 
            color="secondary" 
            onClick={resetForm}
            className="button"
            style={{ marginLeft: '10px' }}
          >
            キャンセル
          </Button>
        )}
      </form>

      <h3>既存のサービス</h3>
      <ul>
        {servicesList.map(service => (
          <li key={service.id} className="admin-list-item">
            {service.imageURL && <img src={service.imageURL} alt={service.title} style={{ width: '150px', height: '100px', objectFit: 'cover' }} />}
            <div>
              <h4>{service.title}</h4>
              <p>{service.shortDescription}</p>
              <Button onClick={() => viewService(service)} variant="outlined" color="primary" style={{ marginRight: '10px' }}>
                プレビュー
              </Button>
              <Button onClick={() => editService(service)} variant="outlined" color="secondary" style={{ marginRight: '10px' }}>
                編集
              </Button>
              <Button onClick={() => deleteService(service.id, service.imageURL)} variant="outlined" color="error">
                削除
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {showOverlay && (
        <Overlay onClose={() => setShowOverlay(false)}>
          <h3>{formState.title}</h3>
          <img src={formState.imageURL} alt={formState.title} style={{ width: '300px', height: '200px', objectFit: 'cover', marginBottom: '1rem' }} />
          <h4>短い説明</h4>
          <p>{formState.shortDescription}</p>
          <h4>詳細な説明</h4>
          <p>{formState.detailedDescription}</p>
        </Overlay>
      )}
    </div>
  );
}

export default ManageServices;
